import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigation } from 'react-router';
import type { ShopperSearch } from '@/scapi';
import { BEST_MATCHES_SORT_ID, isSamePathnameListingDataNavigationPending } from '@/lib/plp-panel-url-state';
import { PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';
import type { PlpSortingOption } from '@/components/eataly-category-plp-sticky-bar';

export function useCategorySorting(result: {
    sortingOptions?: ShopperSearch.schemas['ProductSearchResult']['sortingOptions'];
}) {
    const { t } = useTranslation('categoryPlp');
    const location = useLocation();
    const navigation = useNavigation();

    const SORT = PRODUCT_SEARCH_QUERY_PARAMS.SORT;

    function sortFromSearch(search: string): string | null {
        const params = new URLSearchParams(search);
        return params.get(SORT) || null;
    }
    const isPending = useMemo(
        () =>
            isSamePathnameListingDataNavigationPending({
                pathname: location.pathname,
                search: location.search,
                navigationState: navigation.state,
                navigationLocation: navigation.location,
            }),
        [location.pathname, location.search, navigation.location, navigation.state]
    );

    // No `sort` in URL → engine default (`best-matches` via fetchSearchProducts).
    const effectiveSortingOption =
        (navigation.location && navigation.location.pathname === location.pathname
            ? sortFromSearch(navigation.location.search)
            : sortFromSearch(location.search)) ?? BEST_MATCHES_SORT_ID;

    const sortingOptions = useMemo(() => {
        const relevanceLabel = t('sortOptions.relevance');
        const fromApi = (result?.sortingOptions || []).map((option) =>
            option.id === BEST_MATCHES_SORT_ID ? { ...option, label: relevanceLabel } : option
        );
        const hasBestMatches = fromApi.some((option) => option.id === BEST_MATCHES_SORT_ID);
        if (hasBestMatches) {
            return fromApi as PlpSortingOption[];
        }
        return [{ id: BEST_MATCHES_SORT_ID, label: relevanceLabel }, ...fromApi] as PlpSortingOption[];
    }, [result?.sortingOptions, t]);

    const selectedSortingOptionLabel = useMemo(() => {
        return (
            sortingOptions.find((option) => option.id === effectiveSortingOption)?.label ||
            sortingOptions[0]?.label ||
            ''
        );
    }, [effectiveSortingOption, sortingOptions]);

    return {
        isPending,
        effectiveSortingOption,
        selectedSortingOptionLabel,
        sortingOptions,
    };
}
